/**
 * Auth Recovery Utilities
 * Automatically detects and fixes corrupted auth state
 */

import { supabase } from './supabase'

// Check if auth state is corrupted
export async function isAuthStateCorrupted(): Promise<boolean> {
  try {
    // Check for mismatched auth states
    const localStorage = window.localStorage.getItem('facility-pro-auth')
    const hasLocalAuth = !!localStorage
    
    // Try to get session with a short timeout
    const sessionPromise = supabase.auth.getSession()
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Session check timeout')), 2000)
    )
    
    try {
      const { data: { session }, error } = await Promise.race([
        sessionPromise,
        timeoutPromise
      ]) as { data: { session: unknown }; error: unknown }
      
      const hasValidSession = !error && !!session
      
      // If we have local auth but no valid session, it's corrupted
      if (hasLocalAuth && !hasValidSession) {
        console.warn('🔐 Corrupted auth detected: Local auth exists but no valid session')
        return true
      }
      
      // If session check times out, consider it corrupted
      return false
    } catch (e) {
      if (e.message === 'Session check timeout') {
        console.warn('🔐 Corrupted auth detected: Session check timed out')
        return true
      }
      return false
    }
  } catch (e) {
    console.error('🔐 Error checking auth state:', e)
    return false
  }
}

// Clear all auth-related data
export function clearCorruptedAuth() {
  console.log('🔐 Clearing corrupted auth state...')
  
  // Clear all auth-related localStorage keys
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (
      key.includes('supabase') || 
      key.includes('auth') || 
      key.includes('profile') ||
      key.includes('facility')
    )) {
      keysToRemove.push(key)
    }
  }
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key)
    console.log(`🔐 Removed: ${key}`)
  })
  
  // Clear sessionStorage
  sessionStorage.clear()
  
  // Clear cookies
  document.cookie.split(";").forEach(c => {
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/")
  })
  
  console.log('🔐 Auth state cleared successfully')
}

// Auto-recovery function to run on app start
export async function autoRecoverAuth() {
  console.log('🔐 Checking auth health...')
  
  const isCorrupted = await isAuthStateCorrupted()
  
  if (isCorrupted) {
    console.warn('🔐 Corrupted auth detected, auto-recovering...')
    
    // Show a brief message if we're not on the login page
    if (!window.location.pathname.includes('/login')) {
      // Create a temporary message element
      const message = document.createElement('div')
      message.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #1a1a1a;
        color: white;
        padding: 20px 40px;
        border-radius: 8px;
        font-family: system-ui, -apple-system, sans-serif;
        z-index: 9999;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      `
      message.innerHTML = `
        <h3 style="margin: 0 0 10px 0; color: #D4AF37;">Refreshing Authentication...</h3>
        <p style="margin: 0; color: #ccc;">Please wait a moment.</p>
      `
      document.body.appendChild(message)
    }
    
    clearCorruptedAuth()
    
    // Small delay to show the message
    setTimeout(() => {
      window.location.reload()
    }, 500)
    
    return true
  }
  
  console.log('🔐 Auth state is healthy')
  return false
}